import * as React from 'react';
import * as Yup from 'yup';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { Field, Formik, FieldProps, FormikProps, FormikValues } from 'formik';
import { StyledForm, Button, StyledFormGroup, Box } from '../../components';
import { formatFormValues, formatGqlError, getLoggedInUser, getUserRoles } from '../../utils/helpers';
import Span from '../../typography/Span';
import StyledFormSelect from '../../components/formElements/StyledFormSelect';
import { ApolloError } from 'apollo-client';
import { MutationFn } from 'react-apollo';
import { theme } from '../../theme';
import { omit } from 'lodash';
import { USER_STATUS } from '../../utils/enums';
import { RefreshCcw } from 'react-feather';

type UpdateUserFormValues = {
  role?: string;
  status?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  authCode?: string;
};

type UpdateUserFormProps = RouteComponentProps & {
  userID: string | null;
  initialValues?: FormikValues;
  error?: ApolloError;
  loading?: boolean;
  mutation: MutationFn;
  onClickRemove?: () => void;
  removeButton?: boolean;
};

const UpdateUserFormValidation = Yup.object().shape({
  role: Yup.mixed().required('Required'),
  status: Yup.mixed().required('Required'),
  email: Yup.string()
    .email()
    .required('Required'),
  authCode: Yup.string().required('Required'),
});

const UpdateUserForm: React.FC<UpdateUserFormProps> = ({
  initialValues,
  userID,
  mutation,
  error,
  loading,
  onClickRemove,
  removeButton,
}) => {
  const user = getLoggedInUser();
  return (
    <Formik
      validationSchema={UpdateUserFormValidation}
      enableReinitialize={true}
      initialValues={
        initialValues || {
          role: undefined,
          status: undefined,
          email: '',
          lastName: '',
          firstName: '',
          authCode: '',
        }
      }
      onSubmit={(values: UpdateUserFormValues) => {
        let formattedValues = formatFormValues(values);
        formattedValues = omit(formattedValues, ['image'])
        mutation({
          variables: { ...formattedValues, id: userID },
        });
      }}
      render={(formikProps: FormikProps<UpdateUserFormValues>) => {
        return (
          <StyledForm>
            <Field
              name="role"
              render={({ field }: FieldProps<UpdateUserFormValues>) => (
                <StyledFormSelect
                  width="100%"
                  {...field}
                  field={field}
                  label="Role"
                  form={formikProps}
                  options={getUserRoles(user)}
                />
              )}
            />
            <Field
              name="firstName"
              render={({ field }: FieldProps<UpdateUserFormValues>) => (
                <StyledFormGroup width="100%" {...field} field={field} label="First Name" form={formikProps} />
              )}
            />
            <Field
              name="lastName"
              render={({ field }: FieldProps<UpdateUserFormValues>) => (
                <StyledFormGroup width="100%" {...field} field={field} label="Last Name" form={formikProps} />
              )}
            />
            <Field
              name="email"
              render={({ field }: FieldProps<UpdateUserFormValues>) => (
                <StyledFormGroup width="100%" {...field} field={field} label="Email" form={formikProps} />
              )}
            />
            <Field
              name="authCode"
              render={({ field }: FieldProps<UpdateUserFormValues>) => (
                <StyledFormGroup
                  type="number"
                  width="100%"
                  {...field}
                  field={field}
                  label="Auth Code"
                  form={formikProps}
                  disabled={true}
                  customIcon={<RefreshCcw style={{ marginLeft: 10, cursor: 'pointer' }} onClick={() =>
                    formikProps.setFieldValue('authCode', `000${Math.ceil(Math.random() * 9999)}`.slice(-4))
                  } />}
                />
              )}
            />
            <Field
              name="status"
              render={({ field }: FieldProps<UpdateUserFormValues>) => (
                <StyledFormSelect
                  width="100%"
                  {...field}
                  field={field}
                  label="Status"
                  form={formikProps}
                  options={USER_STATUS}
                />
              )}
            />
            {error && (
              <Span width="100%" textAlign="center" color="error" mb={3}>
                {formatGqlError(error)}
              </Span>
            )}
            <Box width="100%" pt={4} flexDirection="row" alignItems="flex-start" justifyContent="flex-end">
              {removeButton && (
                <Button bg={theme.colors.error} mr={2} type="button" text="Remove" onClick={onClickRemove} />
              )}
              <Button isLoading={loading} type="submit" text="Save & Update" />
            </Box>
          </StyledForm>
        );
      }}
    />
  );
};

export default withRouter(UpdateUserForm);



// WEBPACK FOOTER //
// ./src/forms/admin/UpdateUserForm.tsx